/**
 * The card picker's rules — what is dealt, what a tap does, how the tray fills
 * and where each card sits — kept apart from SelectCards.vue so they can be
 * tested without a browser (see __tests__/card-picker.spec.js).
 *
 * Two layouts, one deck (design-mockups/card-selection-concepts.html and
 * sweeping-spread-at-scale.html):
 *   - 'fan'    the Arc Fan: a held hand splayed from one pivot. Wide screens
 *              with a mouse, where the pointer can aim at a sliver.
 *   - 'ribbon' the Sweeping Spread: a shallow arc that scrolls and snaps.
 *              Phones and tablets: tap to peek, tap again to draw, so a
 *              finger on overlapping cards can never draw the wrong one.
 */

const sameId = (a, b) => a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b)

/**
 * The cards on offer: the dealt deck minus cards someone else already drew
 * (excludeIds, e.g. the sender's cards beneath a Draw deeper) and minus cards
 * already pulled on a resumed draw (matched by id, or by name when the resume
 * payload carries no id). Never mutates `cards`.
 */
export function dealDeck (cards, { excludeIds = [], resumePicked = [] } = {}) {
  const ids = [...(excludeIds || []), ...(resumePicked || []).map(p => p && p.card_id)]
    .filter(v => v !== undefined && v !== null)
  const names = new Set((resumePicked || []).map(p => p && p.name).filter(Boolean))
  return (cards || []).filter(c => !ids.some(id => sameId(id, c.card_id)) && !names.has(c.name))
}

/** Fisher–Yates on a copy. `rand` is injectable so tests are deterministic. */
export function shuffleDeck (cards, rand = Math.random) {
  const out = [...(cards || [])]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[out[i], out[j]] = [out[j], out[i]]
  }
  return out
}

/** The fan needs room and a pointer that can hover; everything else gets the ribbon. */
export const FAN_MIN_WIDTH = 700
export function pickerMode ({ width, hover }) {
  return hover && width >= FAN_MIN_WIDTH ? 'fan' : 'ribbon'
}

/**
 * What a tap on a card does. The fan draws at once; the ribbon peeks first
 * and draws on a second tap of the same card. `full` = every card is drawn.
 */
export function tapCard ({ mode, peeked, cardId, full = false }) {
  if (full) return { action: 'none', peeked: null }
  if (mode === 'fan') return { action: 'draw', peeked: null }
  if (sameId(peeked, cardId)) return { action: 'draw', peeked: null }
  return { action: 'peek', peeked: cardId }
}

/**
 * Shuffle only before the first pick: every pick is saved the moment it is
 * made, so a "reset" after one would leave the reading holding a card the
 * picker no longer shows. `drawn` counts resumed picks too.
 */
export function canShuffle ({ drawn }) {
  return !drawn
}

/**
 * The tray's empty and filled slots for THIS seeker's draw. Position labels
 * are used only when they belong to this spread (same count).
 */
export function traySlots ({ count, picked = [], labels = [] }) {
  const n = Math.max(0, Number(count) || 0)
  const useLabels = Array.isArray(labels) && labels.length === n
  return Array.from({ length: n }, (_, i) => ({
    index: i,
    label: useLabels ? (labels[i] || '') : '',
    filled: !!picked[i],
    card: picked[i] || null,
  }))
}

/** Cards someone else drew, shown face up ahead of this seeker's slots. */
export function inheritedTray (cards) {
  return (Array.isArray(cards) ? cards : [])
    .filter(c => c && c.name)
    .map(c => ({
      name: c.name,
      image_url: c.image_url || '',
      position_label: c.position_label || '',
      reversed: !!c.reversed,
      inherited: true,
    }))
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const deg = r => r * 180 / Math.PI

/** The widest the fan grows on a big screen, so it stays a hand, not a ribbon. */
export const FAN_MAX_WIDTH = 1200

/**
 * The flattest hand that reaches `halfReach` (pivot → outer edge, sideways)
 * while its crown stays `crown` above the floor and the outermost card's
 * lowest corner stays on or above the floor. Returns the half-spread in radians.
 */
function flatHalfSpread (halfReach, crown, cardH, cardW, maxHalf) {
  const hw = cardW / 2
  let lo = 0
  let hi = maxHalf
  for (let k = 0; k < 40; k++) {
    const t = (lo + hi) / 2
    const radius = (halfReach - hw * Math.cos(t)) / Math.sin(t)
    const drop = radius - crown
    if ((radius - cardH) * Math.cos(t) - hw * Math.sin(t) - drop >= 0) lo = t
    else hi = t
  }
  return lo
}

/**
 * The arch whose legs come down to the floor: the outermost cards' lowest
 * corners touch it and their outer corners touch ±halfW. Null when no spread
 * up to `maxHalf` (radians) can do both.
 */
function archToFloor (halfW, crown, cardH, cardW, maxHalf) {
  const hw = cardW / 2
  const radiusAt = (t) => (crown - cardH * Math.cos(t) - hw * Math.sin(t)) / (1 - Math.cos(t))
  const reachAt = (t) => radiusAt(t) * Math.sin(t) + hw * Math.cos(t)
  if (!(reachAt(maxHalf) <= halfW)) return null
  let lo = 1e-3
  let hi = maxHalf
  for (let k = 0; k < 50; k++) {
    const t = (lo + hi) / 2
    if (reachAt(t) > halfW) lo = t
    else hi = t
  }
  const radius = radiusAt(hi)
  if (!(radius > cardH)) return null
  return { half: hi, radius }
}

/**
 * The lowest point of a card at `angleDeg` (y up, from the pivot): its outer
 * bottom corner. A card is never clipped when this stays on or above the floor.
 */
export function cardLowestY (radius, cardH, cardW, angleDeg) {
  const t = Math.abs(angleDeg) * Math.PI / 180
  return (radius - cardH) * Math.cos(t) - (cardW / 2) * Math.sin(t)
}

/**
 * Arc Fan geometry for `n` cards in a stage `width` × `height`. The pivot sits
 * `drop` px below the floor at the bottom centre; `radius` is pivot → card
 * top, and the crown always sits at 92% of the stage height. The spread widens
 * with the deck (a few cards stay a tight hand) up to 120°.
 *
 * A desktop stage is short and wide, so a hand pivoting on its floor was a
 * small half-circle in the middle. When the stage is wider than that hand,
 * the pivot sinks below the floor: a bigger radius, a flatter arc, the same
 * height, and the cards spread across (up to FAN_MAX_WIDTH).
 *
 * `floor` (px from the stage top, default its height) lets the arch's legs
 * come down past the stage, to the bottom of the drawn-card slots, so the
 * slots sit in the arch's opening. `clear` = { halfW, h } is the slots' box
 * (half its width, its height up from the floor); when the opening cannot
 * hold it, the legs stop at the stage's own floor instead. Either way no
 * card's corner ever leaves the stage: not past either side, not under the
 * floor.
 */
export function fanLayout (n, { width, height, floor, clear } = {}) {
  const cardH = clamp(height * 0.5, 60, 190)
  const cardW = cardH * 0.64
  const crownTop = height * 0.08
  const base = { cardW, cardH }
  if (!n) return { ...base, angles: [], spread: 0, radius: height - crownTop, drop: 0, floor: height, legsDown: false }
  const halfW = Math.min(width - 16, FAN_MAX_WIDTH) / 2
  const maxSpread = Math.min(120, n * 8)
  const maxHalf = (maxSpread / 2) * Math.PI / 180
  const angleList = (spread) => Array.from({ length: n }, (_, i) => (n === 1 ? 0 : -spread / 2 + (spread * i) / (n - 1)))

  // The arch down to the slots' bottom, when asked and when it frames them.
  if (n > 1 && floor && floor > height && clear) {
    const crown = floor - crownTop
    const arch = archToFloor(halfW, crown, cardH, cardW, maxHalf)
    if (arch) {
      const drop = arch.radius - crown
      // The slots' top corners must sit inside the arch's inner edge.
      const inner = arch.radius - cardH
      const fits = Math.hypot(clear.halfW, clear.h + drop) < inner - 6
      if (fits) {
        const spread = deg(arch.half) * 2
        return { ...base, angles: angleList(spread), spread, radius: arch.radius, drop, floor, legsDown: true }
      }
    }
  }

  const crown = height - crownTop
  let radius = crown
  let spread
  const hw = cardW / 2
  if (n > 1 && Math.sin(maxHalf) * crown + hw * Math.cos(maxHalf) < halfW) {
    // Room to spare beside the hand: flatten it out to the sides.
    const half = flatHalfSpread(halfW, crown, cardH, cardW, maxHalf)
    radius = Math.max(crown, (halfW - hw * Math.cos(half)) / Math.sin(half))
    spread = deg(half) * 2
  } else if (n === 1) {
    spread = 0
  } else {
    // Pivot on the floor: as wide as the sides allow and the floor holds.
    let lo = 0
    let hi = maxHalf
    for (let k = 0; k < 40; k++) {
      const t = (lo + hi) / 2
      const ok = radius * Math.sin(t) + hw * Math.cos(t) <= halfW && cardLowestY(radius, cardH, cardW, deg(t)) >= 0
      if (ok) lo = t
      else hi = t
    }
    spread = deg(lo) * 2
  }
  const drop = radius - crown
  return { ...base, angles: angleList(spread), spread, radius, drop, floor: height, legsDown: false }
}

/**
 * Where along the hand the pointer is, as a fractional card index (2.5 is
 * halfway between cards 2 and 3), from its angle about the pivot. Held at the
 * ends; -1 with no cards.
 */
export function fanPointerIndex (angles, target) {
  const a = angles || []
  if (!a.length) return -1
  if (a.length === 1 || target <= a[0]) return 0
  if (target >= a[a.length - 1]) return a.length - 1
  for (let i = 0; i < a.length - 1; i++) {
    if (target <= a[i + 1]) return i + (target - a[i]) / ((a[i + 1] - a[i]) || 1)
  }
  return a.length - 1
}

/**
 * Which card is lifted for a pointer at fractional index `f`, given the one
 * lifted now. The lifted card stays until the pointer is `band` of a card past
 * the halfway line, so a hand resting on a boundary never flickers between two.
 */
export function stickyIndex (prev, f, band = 0.3) {
  if (f < 0) return -1
  if (prev >= 0 && Math.abs(f - prev) < 0.5 + band) return prev
  return Math.round(f)
}

/** The hover's feel, in one place. */
export const FAN_HOVER = {
  lift: 10, // px: a gentle rise that stays in the arc's band
  scale: 1.03,
  sigma: 0.8, // in cards: the next card rises about a third as much, the one after barely
  reach: 3, // cards beyond this never move
  ease: 'cubic-bezier(.2, .8, .2, 1)',
  ms: 220,
}

/**
 * How card `d` cards away from the lifted one rises: a soft bell, so the
 * neighbours answer gently and the hand never snaps. `amp` 0 is rest.
 */
export function fanHoverPose (d, { lift = FAN_HOVER.lift, scale = FAN_HOVER.scale, sigma = FAN_HOVER.sigma, amp = 1 } = {}) {
  if (!amp || Math.abs(d) > FAN_HOVER.reach) return { lift: 0, scale: 1 }
  const g = Math.exp(-(d * d) / (2 * sigma * sigma)) * amp
  return { lift: lift * g, scale: 1 + (scale - 1) * g }
}

/**
 * Stacking: the card `center` on top, the cards left of it rising toward it
 * and the cards right of it falling away from it, so the hand opens around
 * the lifted card on both sides. Moving the centre one card changes the
 * z-index of those two cards only (a full pyramid renumbered every card), and
 * swaps exactly one neighbouring pair. Where a left card and a right card
 * meet, the centre card covers them.
 */
export function fanZ (i, center) {
  if (i === center) return 1000
  return i < center ? 1 + i : 999 - i
}

/**
 * The stack's centre walks one card per frame toward the lifted card, so a
 * long jump re-stacks as a quick ripple rather than every card at once.
 */
export function stepToward (cur, target) {
  if (cur === target) return cur
  return cur + (target > cur ? 1 : -1)
}

/**
 * The ribbon's shallow arc: a card `dx` px from the scroller's centre rises
 * and tilts outward, gently, and never past a small cap — so as the strip
 * scrolls, the curve stays under the thumb rather than flinging cards away.
 */
export function ribbonPose (dx, half) {
  const t = clamp(dx / (half || 1), -1.6, 1.6)
  const y = -Math.min(40, 22 * t * t)
  const rot = clamp(t * 11, -16, 16)
  const scale = 1 - Math.min(0.14, 0.09 * Math.abs(t))
  return { y: y === 0 ? 0 : y, rot: rot === 0 ? 0 : rot, scale }
}

/**
 * Is a pointer at (px, py) — relative to the fan's pivot, y up — over the
 * fan's band of cards? Only there may a card lift, because a lifted card is a
 * promise that a click draws it.
 */
export function onFanBand (fan, px, py) {
  const dist = Math.hypot(px, py)
  const ang = Math.atan2(px, Math.max(1e-6, py)) * 180 / Math.PI
  return py > 0
    && dist >= fan.radius - fan.cardH - 24
    && dist <= fan.radius + 24
    && Math.abs(ang) <= fan.spread / 2 + 6
}

/** Below this pane height the tray and its status share one row. */
export const COMPACT_MAX_HEIGHT = 340
export function isCompact (height) {
  return height > 0 && height < COMPACT_MAX_HEIGHT
}
